import { memo, useMemo } from 'react';

import { Elements } from '@stripe/react-stripe-js';
import { type StripeElementsOptions } from '@stripe/stripe-js';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { getEnvVars } from '@order/utils';

import { type StripeElementsProviderProps } from '../../types';
import { getMinimumAmount, loadStripeWithRetry } from '../../utils';

export const StripeElementsProvider = memo((props: StripeElementsProviderProps) => {
  const { children, amount = 0, sessionId, isPollingEnabled } = props;

  const stripePaymentMethodConfig = useFeatureFlag('permanent-stripe-payment-method-config'); // prettier-ignore

  const defaultId = stripePaymentMethodConfig?.defaultId;
  const pollingDisabledId = stripePaymentMethodConfig?.pollingDisabledId;
  const paymentMethodConfiguration = isPollingEnabled? defaultId : pollingDisabledId; // prettier-ignore

  const stripePromise = useMemo(() => loadStripeWithRetry(publicKey), []);

  const options = useMemo<StripeElementsOptions>(
    () => ({
      amount: getMinimumAmount(amount),
      currency,
      mode,
      paymentMethodCreation,
      captureMethod,
      customerSessionClientSecret: sessionId,
      paymentMethodConfiguration,
    }),
    [amount, sessionId, paymentMethodConfiguration],
  );

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const currency = 'usd';
const mode = 'payment';
const paymentMethodCreation = 'manual';
const captureMethod = 'manual';
const publicKey = getEnvVars().STRIPE_PUBLIC_KEY;
